import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Checkbox,
  Rating,
  Divider
} from '@mui/material';
import {
  Psychology as AIIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  LocalHospital as HospitalIcon,
  Phone as PhoneIcon,
  Timer as TimerIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  VolumeUp as VolumeUpIcon,
  KeyboardVoice as VoiceIcon
} from '@mui/icons-material';

interface SymptomAssessment {
  severity: 'emergency' | 'urgent' | 'moderate' | 'mild';
  confidence: number;
  recommendation: string;
  symptoms: string[];
  riskFactors: string[];
  nextSteps: string[];
}

interface VoiceSymptomAssessment {
  severity: 'emergency' | 'urgent' | 'moderate' | 'mild';
  confidence: number;
  recommendation: string;
  symptoms: string[];
  transcription: string;
  requiresTelemedicine: boolean;
  emergencyKeywords: string[];
}

interface SymptomCheckerProps {
  isProxySubmission?: boolean;
  patientName?: string;
  inline?: boolean;
  enableVoice?: boolean;
  onAssessment?: (assessment: SymptomAssessment | VoiceSymptomAssessment) => void;
  onTelemedicineRequest?: (assessment: VoiceSymptomAssessment) => void;
}

const SymptomChecker: React.FC<SymptomCheckerProps> = ({ 
  isProxySubmission = false, 
  patientName = '',
  inline = false,
  enableVoice = false,
  onAssessment,
  onTelemedicineRequest
}) => {
  const [open, setOpen] = useState(inline);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [assessment, setAssessment] = useState<SymptomAssessment | null>(null);
  
  // Voice recording states
  const [useVoiceMode, setUseVoiceMode] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [voiceAssessment, setVoiceAssessment] = useState<VoiceSymptomAssessment | null>(null);
  
  const [symptoms, setSymptoms] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [painLevel, setPainLevel] = useState<number | null>(null);
  const [duration, setDuration] = useState('');
  
  // Voice recording functionality
  const startVoiceRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setIsRecording(true);
      
      // Mock transcription after 3 seconds
      setTimeout(() => {
        const mockTranscriptions = [
          "Tengo dolor en el pecho desde hace una hora, me duele al respirar",
          "Me duele mucho la cabeza y tengo mareos",
          "Siento dificultad para respirar y palpitaciones",
          "Tengo dolor de estómago muy fuerte y náuseas"
        ];
        const mockTranscription = mockTranscriptions[Math.floor(Math.random() * mockTranscriptions.length)];
        setTranscription(mockTranscription);
        
        processVoiceAssessment(mockTranscription);
        setIsRecording(false);
        stream.getTracks().forEach(track => track.stop());
      }, 3000);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('No se pudo acceder al micrófono');
    }
  };

  const processVoiceAssessment = (text: string) => {
    const emergencyKeywords = ['dolor pecho', 'no puedo respirar', 'desmayo', 'sangre'];
    const foundKeywords = emergencyKeywords.filter(keyword => text.toLowerCase().includes(keyword));
    const hasEmergency = foundKeywords.length > 0;
    
    const voiceResult: VoiceSymptomAssessment = {
      severity: hasEmergency ? 'emergency' : text.includes('dolor') ? 'urgent' : 'moderate',
      confidence: hasEmergency ? 0.9 : 0.7,
      recommendation: hasEmergency 
        ? 'SÍNTOMAS DE EMERGENCIA DETECTADOS - Consulta médica inmediata recomendada'
        : 'Síntomas que requieren evaluación médica',
      symptoms: extractSymptomsFromText(text),
      transcription: text,
      requiresTelemedicine: hasEmergency || text.includes('dolor'),
      emergencyKeywords: foundKeywords
    };
    
    setVoiceAssessment(voiceResult);
    onAssessment?.(voiceResult);
    
    if (voiceResult.requiresTelemedicine) {
      onTelemedicineRequest?.(voiceResult);
    }
  };

  const extractSymptomsFromText = (text: string): string[] => {
    const symptomMap = {
      'dolor pecho': 'Dolor en el pecho',
      'dolor cabeza': 'Dolor de cabeza',
      'mareo': 'Mareos',
      'náusea': 'Náuseas',
      'respirar': 'Dificultad para respirar',
      'palpitaciones': 'Palpitaciones'
    };
    
    return Object.entries(symptomMap)
      .filter(([keyword]) => text.toLowerCase().includes(keyword))
      .map(([, symptom]) => symptom);
  };

  // Mock patient data - in real app this would come from user context/store
  const patientData = {
    age: 68,
    medicalHistory: ['Hipertensión', 'Diabetes', 'Historial de ACV']
  };

  const commonSymptoms = [
    'Dolor en el pecho',
    'Dificultad para respirar',
    'Mareos',
    'Náuseas',
    'Dolor de cabeza',
    'Palpitaciones',
    'Sudoración excesiva',
    'Debilidad en brazos/piernas',
    'Confusión',
    'Dolor abdominal',
    'Fiebre',
    'Dolor de espalda'
  ];


  const steps = ['Síntomas', 'Evaluación'];

  const handleSymptomToggle = (symptom: string) => {
    setSymptoms(prev => 
      prev.includes(symptom) 
        ? prev.filter(s => s !== symptom)
        : [...prev, symptom]
    );
  };


  const assessSymptoms = async () => {
    setLoading(true);
    
    try {
      const response = await fetch('http://localhost:3000/api/v1/symptoms/assess', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          symptoms,
          description,
          painLevel,
          duration,
          age: patientData.age,
          medicalHistory: patientData.medicalHistory,
          submittedBy: isProxySubmission ? 'family_member' : 'patient',
          patientName: isProxySubmission ? patientName : null
        })
      });

      const data = await response.json();
      
      if (data.success) {
        const newAssessment = {
          severity: data.data.severity,
          confidence: data.data.confidence,
          recommendation: data.data.recommendation,
          symptoms,
          riskFactors: data.data.riskFactors,
          nextSteps: data.data.nextSteps
        };
        setAssessment(newAssessment);
        onAssessment?.(newAssessment);
      } else {
        throw new Error('Failed to assess symptoms');
      }
    } catch (error) {
      console.error('Error assessing symptoms:', error);
      // Fallback to basic assessment
      const fallbackAssessment = {
        severity: 'moderate' as const,
        confidence: 0.5,
        recommendation: 'No pudimos procesar su evaluación. Por favor consulte a un médico.',
        symptoms,
        riskFactors: patientData.medicalHistory,
        nextSteps: ['Consulte a su médico lo antes posible', 'Monitoree sus síntomas']
      };
      setAssessment(fallbackAssessment);
      onAssessment?.(fallbackAssessment);
    } finally {
      setLoading(false);
      setStep(1);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'emergency': return 'error';
      case 'urgent': return 'warning';
      case 'moderate': return 'info';
      case 'mild': return 'success';
      default: return 'default';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'emergency': return <WarningIcon />;
      case 'urgent': return <TimerIcon />;
      case 'moderate': return <InfoIcon />;
      case 'mild': return <CheckIcon />;
      default: return <InfoIcon />;
    }
  };

  const reset = () => {
    setStep(0);
    setSymptoms([]);
    setDescription('');
    setPainLevel(null);
    setDuration('');
    setAssessment(null);
    setOpen(false);
  };

  if (inline) {
    return (
      <Card sx={{ bgcolor: '#e3f2fd', height: '100%' }}>
        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AIIcon color="primary" />
              <Typography variant="h6" color="primary">
                Evaluador de Síntomas IA
              </Typography>
            </Box>
            {enableVoice && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant={useVoiceMode ? 'contained' : 'outlined'}
                  size="small"
                  startIcon={<VoiceIcon />}
                  onClick={() => setUseVoiceMode(!useVoiceMode)}
                >
                  Voz
                </Button>
              </Box>
            )}
          </Box>
          
          {/* Voice Mode Interface */}
          {useVoiceMode ? (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography variant="body1" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
                Presione el botón y describa sus síntomas con voz natural
              </Typography>
              
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Button
                  variant="contained"
                  color={isRecording ? 'error' : 'success'}
                  size="large"
                  onClick={startVoiceRecording}
                  disabled={isRecording}
                  startIcon={isRecording ? <MicOffIcon /> : <MicIcon />}
                  sx={{
                    minHeight: '60px',
                    px: 4,
                    fontSize: '1.1rem',
                    animation: isRecording ? 'pulse 1.5s infinite' : 'none',
                    '@keyframes pulse': {
                      '0%': { transform: 'scale(1)' },
                      '50%': { transform: 'scale(1.05)' },
                      '100%': { transform: 'scale(1)' }
                    }
                  }}
                >
                  {isRecording ? 'Grabando...' : 'Presione para hablar'}
                </Button>
              </Box>

              {transcription && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    Lo que escuchamos:
                  </Typography>
                  <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                    "{transcription}"
                  </Typography>
                </Box>
              )}

              {voiceAssessment && (
                <Alert 
                  severity={voiceAssessment.severity === 'emergency' ? 'error' : 
                           voiceAssessment.severity === 'urgent' ? 'warning' : 'info'}
                  sx={{ mt: 2 }}
                >
                  <Typography variant="body2" fontWeight="bold">
                    {voiceAssessment.recommendation}
                  </Typography>
                  <Typography variant="caption">
                    Confianza: {(voiceAssessment.confidence * 100).toFixed(0)}%
                  </Typography>
                </Alert>
              )}
            </Box>
          ) : step === 0 ? (
            /* Text Mode Interface */
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" gutterBottom>
                ¿Qué síntomas está experimentando?
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {commonSymptoms.map((symptom) => (
                  <Chip
                    key={symptom}
                    label={symptom}
                    clickable
                    size="small"
                    color={symptoms.includes(symptom) ? 'primary' : 'default'}
                    onClick={() => handleSymptomToggle(symptom)}
                  />
                ))}
              </Box>
              
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Otros síntomas"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                sx={{ mb: 2 }}
                size="small"
              />
              
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" component="legend">Dolor (1-10):</Typography>
                <Rating
                  value={painLevel}
                  onChange={(event, newValue) => setPainLevel(newValue)}
                  max={10}
                  size="small"
                />
              </Box>
              
              <Button 
                onClick={assessSymptoms}
                disabled={symptoms.length === 0 || loading}
                variant="contained"
                color="primary"
                fullWidth
                startIcon={loading ? <CircularProgress size={20} /> : <AIIcon />}
              >
                {loading ? 'Evaluando...' : 'Evaluar Síntomas'}
              </Button>
            </Box>
          ) : null}

          {!useVoiceMode && step === 1 && assessment && (
            <Box>
              <Alert 
                severity={getSeverityColor(assessment.severity) as any}
                icon={getSeverityIcon(assessment.severity)}
                sx={{ mb: 2 }}
              >
                <Typography variant="h6">
                  {assessment.severity === 'emergency' ? 'EMERGENCIA MÉDICA' :
                   assessment.severity === 'urgent' ? 'URGENTE' :
                   assessment.severity === 'moderate' ? 'MODERADO' : 'LEVE'}
                </Typography>
                <Typography variant="body2">{assessment.recommendation}</Typography>
              </Alert>

              {assessment.severity === 'emergency' && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="bold">
                    ⚠️ Se recomienda activar emergencia inmediatamente
                  </Typography>
                </Alert>
              )}
              
              <Button onClick={reset} variant="outlined" fullWidth>
                Nueva Evaluación
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card sx={{ bgcolor: '#e3f2fd', cursor: 'pointer' }} onClick={() => setOpen(true)}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <AIIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
          <Typography variant="h6" color="primary">
            Evaluador de Síntomas IA
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {isProxySubmission 
              ? `Reportar síntomas de ${patientName}`
              : 'Evaluación inteligente de síntomas'}
          </Typography>
        </CardContent>
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AIIcon color="primary" />
            Evaluador de Síntomas IA
            {isProxySubmission && (
              <Chip 
                label={`Para ${patientName}`} 
                color="secondary" 
                size="small" 
                sx={{ ml: 2 }}
              />
            )}
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {isProxySubmission && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Está reportando síntomas en nombre de {patientName}. Asegúrese de describir los síntomas que {patientName} está experimentando.
            </Alert>
          )}
          
          <Stepper activeStep={step} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {step === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {isProxySubmission 
                  ? `¿Qué síntomas está experimentando ${patientName}?`
                  : '¿Qué síntomas está experimentando?'}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {commonSymptoms.map((symptom) => (
                  <Chip
                    key={symptom}
                    label={symptom}
                    clickable
                    color={symptoms.includes(symptom) ? 'primary' : 'default'}
                    onClick={() => handleSymptomToggle(symptom)}
                  />
                ))}
              </Box>
              
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Describa otros síntomas"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                sx={{ mb: 2 }}
              />
              
              <Box sx={{ mb: 2 }}>
                <Typography component="legend">Nivel de dolor (1-10)</Typography>
                <Rating
                  value={painLevel}
                  onChange={(event, newValue) => setPainLevel(newValue)}
                  max={10}
                  size="large"
                />
              </Box>
              
              <TextField
                fullWidth
                label="¿Cuánto tiempo ha tenido estos síntomas?"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                placeholder="Ej: 2 horas, 1 día"
              />
            </Box>
          )}


          {step === 1 && (
            <Box>
              {loading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress />
                  <Typography sx={{ mt: 2 }}>
                    Analizando sus síntomas con IA...
                  </Typography>
                </Box>
              ) : assessment && (
                <Box>
                  <Alert 
                    severity={getSeverityColor(assessment.severity) as any}
                    icon={getSeverityIcon(assessment.severity)}
                    sx={{ mb: 3 }}
                  >
                    <Typography variant="h6">
                      {assessment.severity === 'emergency' ? 'EMERGENCIA MÉDICA' :
                       assessment.severity === 'urgent' ? 'URGENTE' :
                       assessment.severity === 'moderate' ? 'MODERADO' : 'LEVE'}
                    </Typography>
                    <Typography>{assessment.recommendation}</Typography>
                    <Typography variant="caption">
                      Confianza: {(assessment.confidence * 100).toFixed(0)}%
                    </Typography>
                  </Alert>

                  <Typography variant="h6" gutterBottom>
                    Pasos recomendados:
                  </Typography>
                  <List>
                    {assessment.nextSteps.map((step, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <CheckIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={step} />
                      </ListItem>
                    ))}
                  </List>

                  {assessment.severity === 'emergency' && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#ffebee', borderRadius: 1 }}>
                      <Typography variant="h6" color="error" gutterBottom>
                        ⚠️ Acción Inmediata Requerida
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                        <Button
                          variant="contained"
                          color="error"
                          startIcon={<WarningIcon />}
                          onClick={() => {/* Trigger emergency alert */}}
                        >
                          Activar Emergencia
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<PhoneIcon />}
                          onClick={() => window.open('tel:131')}
                        >
                          Llamar 131
                        </Button>
                      </Box>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />
                  <Typography variant="caption" color="text.secondary">
                    Esta evaluación es solo una guía. Siempre consulte con un profesional médico para diagnósticos precisos.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {step === 0 && (
            <Button 
              onClick={assessSymptoms}
              disabled={symptoms.length === 0}
              variant="contained"
              color="primary"
            >
              Evaluar Síntomas
            </Button>
          )}
          {step === 1 && (
            <Button onClick={reset} variant="contained">
              Nueva Evaluación
            </Button>
          )}
          <Button onClick={() => setOpen(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SymptomChecker;